import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, User, Heart, ShoppingCart, MapPin, LogOut, Package, ChevronDown, Menu, X, UserCircle, Phone } from 'lucide-react';
import { useCart } from '@/context/CartContext';
import { useAuth } from '@/context/AuthContext';
import { useAddress } from '@/context/AddressContext';
import { useWishlist } from '@/context/WishlistContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import CartDrawer from '@/components/cart/CartDrawer';
import AddressModal from '@/components/address/AddressModal';
import SearchBar from '@/components/search/SearchBar';
import { toast } from 'sonner';

const MainHeader = () => {
  const navigate = useNavigate();
  const { cartCount } = useCart();
  const { user, isAuthenticated, logout } = useAuth();
  const { selectedAddress } = useAddress();
  const { wishlistCount } = useWishlist();
  const [cartOpen, setCartOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [addressModalOpen, setAddressModalOpen] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);

  const handleLogout = async () => {
    try {
      await logout();
      toast.success('Logged out successfully');
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
    }
    setUserMenuOpen(false);
  };

  const handleLocationClick = () => {
    if (isAuthenticated) {
      setAddressModalOpen(true);
    } else {
      toast.info('Please login to manage delivery addresses');
      navigate('/login');
    }
  };

  // Display city from selected address or default
  const displayCity = selectedAddress?.city || 'Select Location';

  return (
    <>
      <header className="sticky top-0 z-50 bg-white border-b border-slate-200 shadow-sm">
        {/* Main Header Row */}
        <div className="max-w-7xl mx-auto px-3 sm:px-4">
          <div className="flex items-center gap-2 sm:gap-4 h-14 sm:h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-1 flex-shrink-0">
              <span className="text-xl sm:text-2xl font-outfit font-bold tracking-tighter text-slate-900">
                DXB<span className="text-[#0047AB]">2</span>BOM
              </span>
            </Link>

            {/* Delivery Location - Desktop Only */}
            <button
              onClick={handleLocationClick}
              className="hidden lg:flex items-center gap-1 text-sm text-slate-600 flex-shrink-0 border-l border-slate-200 pl-4 hover:text-[#0047AB] transition-colors group"
              data-testid="location-selector"
            >
              <MapPin className="w-4 h-4 text-[#0047AB] group-hover:scale-110 transition-transform" />
              <div className="leading-tight text-left">
                <p className="text-[10px] text-slate-400">Deliver to</p>
                <p className="font-semibold text-slate-900 group-hover:text-[#0047AB] flex items-center gap-1">
                  {displayCity}
                  <ChevronDown className="w-3 h-3" />
                </p>
              </div>
            </button>

            {/* Search Bar - Desktop */}
            <div className="hidden sm:block flex-1 max-w-2xl mx-2 lg:mx-4">
              <SearchBar className="w-full" />
            </div>

            {/* Right Actions */}
            <div className="flex items-center gap-1 sm:gap-2 ml-auto">
              {/* Mobile Search Toggle */}
              <Button 
                variant="ghost" 
                size="icon" 
                className="sm:hidden text-slate-700"
                onClick={() => setMobileSearchOpen(!mobileSearchOpen)}
              >
                <Search className="w-5 h-5" />
              </Button>

              {/* User Account / Login */}
              {isAuthenticated ? (
                <div className="relative">
                  <Button 
                    variant="ghost" 
                    className="flex items-center gap-1 sm:gap-2 text-slate-700 hover:text-[#0047AB] px-2 sm:px-3"
                    onClick={() => setUserMenuOpen(!userMenuOpen)}
                    data-testid="user-menu-btn"
                  >
                    {user?.picture ? (
                      <img src={user.picture} alt={user.name} className="w-6 h-6 sm:w-7 sm:h-7 rounded-full" />
                    ) : (
                      <div className="w-6 h-6 sm:w-7 sm:h-7 rounded-full bg-[#0047AB] flex items-center justify-center text-white text-xs sm:text-sm font-semibold">
                        {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                      </div>
                    )}
                    <span className="hidden sm:block text-sm font-medium max-w-[100px] truncate">{user?.name?.split(' ')[0]}</span>
                    <ChevronDown className="w-3 h-3 sm:w-4 sm:h-4 hidden sm:block" />
                  </Button>

                  {/* User Dropdown Menu */}
                  {userMenuOpen && (
                    <>
                      <div 
                        className="fixed inset-0 z-40" 
                        onClick={() => setUserMenuOpen(false)}
                      />
                      <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-slate-200 py-2 z-50">
                        <div className="px-4 py-2 border-b border-slate-100">
                          <p className="font-medium text-slate-900 truncate">{user?.name}</p>
                          <p className="text-xs text-slate-500 truncate">{user?.email}</p>
                        </div>
                        <Link 
                          to="/profile" 
                          className="flex items-center gap-2 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
                          onClick={() => setUserMenuOpen(false)}
                          data-testid="my-profile-link"
                        >
                          <UserCircle className="w-4 h-4" />
                          My Profile
                        </Link>
                        <Link 
                          to="/my-orders" 
                          className="flex items-center gap-2 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
                          onClick={() => setUserMenuOpen(false)}
                          data-testid="my-orders-link"
                        >
                          <Package className="w-4 h-4" />
                          My Orders
                        </Link>
                        <Link 
                          to="/wishlist" 
                          className="flex items-center gap-2 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 sm:hidden"
                          onClick={() => setUserMenuOpen(false)}
                        >
                          <Heart className="w-4 h-4" />
                          Wishlist {wishlistCount > 0 && `(${wishlistCount})`}
                        </Link>
                        <button 
                          onClick={() => {
                            setUserMenuOpen(false);
                            setAddressModalOpen(true);
                          }}
                          className="flex items-center gap-2 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 w-full"
                          data-testid="manage-addresses-link"
                        >
                          <MapPin className="w-4 h-4" />
                          Manage Addresses
                        </button>
                        <Link 
                          to="/contact" 
                          className="flex items-center gap-2 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
                          onClick={() => setUserMenuOpen(false)}
                          data-testid="contact-us-link"
                        >
                          <Phone className="w-4 h-4" />
                          Contact Us
                        </Link>
                        <button 
                          onClick={handleLogout}
                          className="flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50 w-full"
                          data-testid="logout-btn"
                        >
                          <LogOut className="w-4 h-4" />
                          Logout
                        </button>
                      </div>
                    </>
                  )}
                </div>
              ) : (
                <Link to="/login">
                  <Button variant="ghost" className="flex items-center gap-1 sm:gap-2 text-slate-700 hover:text-[#0047AB] px-2 sm:px-3" data-testid="login-btn">
                    <User className="w-5 h-5" />
                    <span className="text-xs sm:text-sm font-medium">Login</span>
                  </Button>
                </Link>
              )}

              {/* Wishlist - Desktop */}
              <Link to={isAuthenticated ? "/wishlist" : "/login"} className="hidden sm:block">
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="relative text-slate-700 hover:text-red-500"
                  data-testid="wishlist-header-btn"
                  onClick={() => !isAuthenticated && toast.info('Please login to view wishlist')}
                >
                  <Heart className="w-5 h-5" />
                  {wishlistCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">
                      {wishlistCount > 99 ? '99+' : wishlistCount}
                    </span>
                  )}
                </Button>
              </Link>

              {/* Wishlist - Mobile */}
              <Link to={isAuthenticated ? "/wishlist" : "/login"} className="sm:hidden">
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="relative text-slate-700 hover:text-red-500"
                  data-testid="wishlist-mobile-btn"
                  onClick={() => !isAuthenticated && toast.info('Please login to view wishlist')}
                >
                  <Heart className="w-5 h-5" />
                  {wishlistCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">
                      {wishlistCount > 99 ? '99+' : wishlistCount}
                    </span>
                  )}
                </Button>
              </Link>

              {/* Cart - Desktop Only (Mobile uses bottom nav) */}
              <Sheet open={cartOpen} onOpenChange={setCartOpen}>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="icon" className="relative text-slate-700 hover:text-[#0047AB] hidden sm:flex">
                    <ShoppingCart className="w-5 h-5" />
                    {cartCount > 0 && (
                      <span className="absolute -top-1 -right-1 bg-[#FF5F1F] text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">
                        {cartCount > 99 ? '99+' : cartCount}
                      </span>
                    )}
                  </Button>
                </SheetTrigger>
                <SheetContent className="w-full sm:max-w-md">
                  <SheetHeader>
                    <SheetTitle className="flex items-center gap-2">
                      <ShoppingCart className="w-5 h-5" />
                      Shopping Cart
                    </SheetTitle>
                  </SheetHeader>
                  <CartDrawer onClose={() => setCartOpen(false)} />
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>

        {/* Mobile Search Bar - Expandable */}
        {mobileSearchOpen && (
          <div className="sm:hidden px-3 pb-3 bg-white border-t border-slate-100">
            <div className="relative">
              <SearchBar 
                className="w-full" 
                onSearch={() => setMobileSearchOpen(false)}
              />
              <button
                type="button"
                onClick={() => setMobileSearchOpen(false)}
                className="absolute right-12 top-1/2 -translate-y-1/2 p-2 text-slate-400 hover:text-slate-600"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </header>

      {/* Address Modal */}
      <AddressModal 
        isOpen={addressModalOpen} 
        onClose={() => setAddressModalOpen(false)} 
      />
    </>
  );
};

export default MainHeader;
